/**
 * Where a request stands. NUMERIC on the wire — no string enum converter is configured — so this
 * is an `as const` object of the server's own numbers, never a string union.
 */
export const ATTRITION_STATUS = {
  pendingDocuments: 0,
  forReview: 1,
  completed: 2,
  cancelled: 3,
} as const;

export type AttritionRequestStatus = (typeof ATTRITION_STATUS)[keyof typeof ATTRITION_STATUS];

/** Where one document stands with its reviewer. Numeric, mirroring the server. */
export const ATTRITION_REVIEW = {
  notSubmitted: 0,
  forReview: 1,
  approved: 2,
  returned: 3,
} as const;

export type AttritionDocumentReviewStatus = (typeof ATTRITION_REVIEW)[keyof typeof ATTRITION_REVIEW];

/** Where one org unit's clearance stands. Numeric, mirroring the server. Cleared is final; Not cleared is not. */
export const ATTRITION_CLEARANCE = {
  pending: 0,
  cleared: 1,
  notCleared: 2,
} as const;

export type AttritionClearanceStatus = (typeof ATTRITION_CLEARANCE)[keyof typeof ATTRITION_CLEARANCE];

/** One row of the register. It carries the employee's personId and NO name. */
export interface AttritionRequestListItem {
  id: string;
  requestNumber: string;
  status: AttritionRequestStatus;
  statusLabel: string;
  personnelId: string;
  /** Omitted from the wire where the employee is above the reader's clearance — read with `!= null`. */
  personId?: string | null;
  employeeNumber?: string | null;
  attritionCategoryId: string;
  categoryName: string;
  effectiveDate?: string | null;
  documentCount: number;
  approvedCount: number;
  /** The org units asked to clear the employee — zero where the category asks for none. */
  clearanceCount: number;
  clearedCount: number;
  /** Documents AND clearances waiting on the CALLER. */
  awaitingMeCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AttritionRequestDocument {
  id: string;
  attritionCategoryDocumentId: string;
  name: string;
  description?: string | null;
  reviewerUserId: string;
  reviewerName?: string | null;
  /** Whether the CALLER is this line's reviewer. */
  isMine: boolean;
  fileName?: string | null;
  /** The stored file's media type, VERIFIED by magic bytes on write — what the preview keys on. */
  contentType?: string | null;
  sizeBytes?: number | null;
  submittedAt?: string | null;
  submittedByName?: string | null;
  reviewStatus: AttritionDocumentReviewStatus;
  reviewStatusLabel: string;
  reviewRemarks?: string | null;
  reviewedAt?: string | null;
  reviewedByName?: string | null;
}

/** One org unit's clearance on a request. */
export interface AttritionRequestClearance {
  id: string;
  attritionCategoryClearanceId: string;
  orgUnitId: string;
  orgUnitName: string;
  signatoryUserId: string;
  signatoryName?: string | null;
  /** Whether the CALLER is this line's signatory. */
  isMine: boolean;
  status: AttritionClearanceStatus;
  statusLabel: string;
  /** What is still owed, on Not cleared. */
  remarks?: string | null;
  decidedAt?: string | null;
  decidedByName?: string | null;
}

export interface AttritionRequestDetail {
  id: string;
  requestNumber: string;
  status: AttritionRequestStatus;
  statusLabel: string;
  personnelId: string;
  personId?: string | null;
  employeeNumber?: string | null;
  attritionCategoryId: string;
  categoryName: string;
  effectiveDate?: string | null;
  remarks?: string | null;
  /** The checklist as it was asked of this request. */
  documents: AttritionRequestDocument[];
  /** The org units asked to clear the employee, as the category listed them at filing. */
  clearances: AttritionRequestClearance[];
  /** Live and still open — what the form, the upload and the cancel key on. */
  isOpen: boolean;
  cancelReason?: string | null;
  cancelledAt?: string | null;
  cancelledByName?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AttritionRequestListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  category?: string;
  awaitingMe?: boolean;
}

/** Create and edit. The number, the status, the checklist and every stamp are the server's. */
export interface AttritionRequestRequest {
  personnelId: string | null;
  attritionCategoryId: string | null;
  effectiveDate: string | null;
  remarks: string | null;
}

/** The module's one list of what its writes touch — read by every History Logs surface. */
export const ATTRITION_REQUEST_AUDIT_ENTITY_TYPES = ['AttritionRequest', 'AttritionRequestDocument', 'AttritionRequestClearance'];
